/**
 * Places collection promo blocks at absolute grid positions (1-based),
 * counting both products and promos in the infinite-scroll grid.
 *
 * Liquid renders products only + a hidden promo stash. This module inserts
 * each promo at data-promo-position whenever enough cells exist (re-runs
 * after infinite scroll appends more products).
 */

const PLACE_DEBOUNCE_MS = 100;

/**
 * @typedef {object} PromoGridState
 * @property {boolean} placing
 * @property {ReturnType<typeof setTimeout> | undefined} timer
 * @property {MutationObserver | null} observer
 * @property {Element | null} observedGrid
 */

/** @type {WeakMap<Element, PromoGridState>} */
const stateByResultsList = new WeakMap();

/**
 * @returns {PromoGridState}
 */
function createState() {
  return { placing: false, timer: undefined, observer: null, observedGrid: null };
}

/**
 * @param {Element} resultsList
 */
function getStash(resultsList) {
  return resultsList.querySelector('[data-collection-promo-stash]');
}

/**
 * @param {Element} resultsList
 */
function getGrid(resultsList) {
  return resultsList.querySelector('[ref="grid"]');
}

/**
 * @param {HTMLElement} el
 * @param {Element[]} items
 * @param {number} index
 */
function assignPromoPage(el, items, index) {
  const neighbor = items[index] || items[index - 1];
  const page = neighbor instanceof HTMLElement ? neighbor.dataset.page : undefined;
  el.dataset.page = page && page !== '' ? page : '1';
}

/**
 * @param {Element} resultsList
 */
function placePromos(resultsList) {
  const state = stateByResultsList.get(resultsList);
  if (!state || state.placing) return;

  const grid = getGrid(resultsList);
  const stash = getStash(resultsList);
  if (!(grid instanceof HTMLElement) || !(stash instanceof HTMLElement)) return;

  // Filters may replace the grid node — keep the observer on the live grid.
  ensureGridObserver(resultsList, grid);

  const products = [...grid.querySelectorAll(':scope > [data-product-id]')];
  const promos = [
    ...grid.querySelectorAll(':scope > .promo-block[data-promo-position]'),
    ...stash.querySelectorAll(':scope > .promo-block[data-promo-position]'),
  ]
    .map((el) => ({
      el,
      position: Number(el.getAttribute('data-promo-position')),
      id: el.getAttribute('data-promo-id') || '',
    }))
    .filter((p) => Number.isFinite(p.position) && p.position > 0)
    .filter((p, i, arr) => arr.findIndex((x) => x.el === p.el) === i)
    .sort((a, b) => a.position - b.position || a.id.localeCompare(b.id));

  /** @type {Element[]} */
  const items = [...products];
  /** @type {Element[]} */
  const waitingInStash = [];

  for (const promo of promos) {
    const index = promo.position - 1;
    if (index < 0) continue;

    // Not enough cells yet (e.g. position 30 with only 24 products loaded)
    if (index > items.length) {
      waitingInStash.push(promo.el);
      continue;
    }

    if (promo.el instanceof HTMLElement) {
      assignPromoPage(promo.el, items, index);
    }
    items.splice(index, 0, promo.el);
  }

  const current = [...grid.children];
  const alreadyCorrect =
    current.length === items.length && current.every((el, i) => el === items[i]);

  for (const el of waitingInStash) {
    if (el.parentElement !== stash) {
      stash.appendChild(el);
    }
  }

  if (alreadyCorrect) return;

  state.placing = true;
  try {
    const fragment = document.createDocumentFragment();
    for (const item of items) {
      fragment.appendChild(item);
    }
    grid.appendChild(fragment);
  } finally {
    requestAnimationFrame(() => {
      state.placing = false;
    });
  }
}

/**
 * @param {Element} resultsList
 */
function schedulePlacePromos(resultsList) {
  let state = stateByResultsList.get(resultsList);
  if (!state) {
    state = createState();
    stateByResultsList.set(resultsList, state);
  }

  window.clearTimeout(state.timer);
  state.timer = setTimeout(() => placePromos(resultsList), PLACE_DEBOUNCE_MS);
}

/**
 * @param {MutationRecord[]} mutations
 */
function mutationsAddedProducts(mutations) {
  for (const mutation of mutations) {
    for (const node of mutation.addedNodes) {
      if (!(node instanceof Element)) continue;
      if (node.hasAttribute('data-product-id')) return true;
      if (node.querySelector?.('[data-product-id]')) return true;
    }
  }
  return false;
}

/**
 * @param {Element} resultsList
 * @param {Element} grid
 */
function ensureGridObserver(resultsList, grid) {
  const state = stateByResultsList.get(resultsList);
  if (!state) return;

  if (state.observedGrid === grid && state.observer) return;

  state.observer?.disconnect();
  state.observedGrid = grid;
  state.observer = new MutationObserver((mutations) => {
    if (state.placing) return;
    if (!mutationsAddedProducts(mutations)) return;
    schedulePlacePromos(resultsList);
  });
  state.observer.observe(grid, { childList: true });
}

/**
 * @param {Element} resultsList
 */
function bindResultsList(resultsList) {
  if (stateByResultsList.has(resultsList)) {
    const grid = getGrid(resultsList);
    if (grid) ensureGridObserver(resultsList, grid);
    schedulePlacePromos(resultsList);
    return;
  }

  const state = createState();
  stateByResultsList.set(resultsList, state);

  const grid = getGrid(resultsList);
  if (grid) ensureGridObserver(resultsList, grid);

  schedulePlacePromos(resultsList);
}

/**
 * Reset observers after section morph / filter updates.
 * @param {Element} resultsList
 */
function rebindResultsList(resultsList) {
  const state = stateByResultsList.get(resultsList);
  if (state) {
    state.observer?.disconnect();
    window.clearTimeout(state.timer);
    stateByResultsList.delete(resultsList);
  }
  bindResultsList(resultsList);
}

function scan() {
  for (const resultsList of document.querySelectorAll('results-list')) {
    if (getStash(resultsList)) {
      bindResultsList(resultsList);
    }
  }
}

scan();
document.addEventListener('DOMContentLoaded', scan);

const rootObserver = new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    for (const node of mutation.addedNodes) {
      if (!(node instanceof Element)) continue;
      if (node.matches?.('results-list') || node.querySelector?.('results-list')) {
        scan();
        return;
      }
    }
  }
});

if (document.body) {
  rootObserver.observe(document.body, { childList: true, subtree: true });
} else {
  document.addEventListener('DOMContentLoaded', () => {
    rootObserver.observe(document.body, { childList: true, subtree: true });
  });
}

document.addEventListener('filter:update', () => {
  requestAnimationFrame(() => {
    for (const resultsList of document.querySelectorAll('results-list')) {
      if (getStash(resultsList)) rebindResultsList(resultsList);
    }
  });
});
